import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSelect from "components/SoftSelect";

import { useAuth } from "context/AuthContext";
import {
  saveUser,
  getAirports,
  getNonProfits,
} from "./Users/services/graphqlService";
import {
  canAssignAviPairAdminRole,
  isAviPairAdminAssociation,
} from "./missionOwnerUtils";

const ROLE_OPTIONS = [
  { value: "User", label: "General User" },
  { value: "Admin", label: "Admin" },
  { value: "Staff", label: "Staff" },
];

// All client type options
const CLIENT_TYPE_OPTIONS = [
  { value: "AviPair", label: "Avipair" },
  { value: "User", label: "General User" },
  { value: "Admin", label: "Admin" },
  { value: "Airport", label: "Airport" },
  { value: "NonProfit", label: "Non-Profit" },
  { value: "Pilot", label: "Pilot" },
];

// Allowed client types (entity types) per role: General User → User/Pilot; Admin → all; Staff → Airport/NonProfit
const CLIENT_TYPES_BY_ROLE = {
  User: ["User", "Pilot"],
  Admin: ["AviPair", "Admin", "NonProfit"],
  Staff: ["Airport", "NonProfit"],
};

const nextAssociationId = () =>
  `a-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
].map((state) => ({ value: state, label: state }));

// Common timezones
const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
  { value: "UTC", label: "UTC" },
];

// Common locales
const LOCALE_OPTIONS = [
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "es-ES", label: "Spanish (Spain)" },
  { value: "es-MX", label: "Spanish (Mexico)" },
  { value: "fr-FR", label: "French (France)" },
  { value: "de-DE", label: "German (Germany)" },
  { value: "it-IT", label: "Italian (Italy)" },
  { value: "pt-BR", label: "Portuguese (Brazil)" },
  { value: "ja-JP", label: "Japanese (Japan)" },
  { value: "zh-CN", label: "Chinese (Simplified)" },
];

function AddUserPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user: authUser, tokenRole, tokenEntityType } = useAuth();
  const canAssignAviPairAdmin = canAssignAviPairAdminRole({
    tokenRole,
    tokenEntityType,
  });

  const profileId = searchParams.get("profileId");
  const isEditMode = !!profileId;
  const userDataFromState = location.state?.userData;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    mobile: "",
    timezone: "America/New_York",
    locale: "en-US",
    associations: [
      { id: nextAssociationId(), role: "User", entityType: "", entityId: "" },
    ],
    employeeId: "",
    preference: "",
    city: "",
    state: "",
    locationId: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Phone number validation function - US format: (123) 123-1312
  const validatePhoneNumber = (phone) => {
    if (!phone || !phone.trim()) return null;
    // Remove all non-digit characters and check length
    const digitsOnly = phone.replace(/\D/g, "");
    if (digitsOnly.length !== 10) {
      return "Phone number must be exactly 10 digits in format (123) 123-1234";
    }
    // Check if the format matches (XXX) XXX-XXXX
    const usPhoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (!usPhoneRegex.test(phone.trim())) {
      return "Phone number must be in format (123) 123-1234";
    }
    return null;
  };

  // Phone number formatting function - US format: (123) 123-1234
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, "");
    // Limit to exactly 10 digits for US format
    const limitedDigits = digitsOnly.slice(0, 10);

    // Format as (XXX) XXX-XXXX for US numbers (exactly 10 digits)
    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    } else {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(
        3,
        6,
      )}-${limitedDigits.slice(6)}`;
    }
  };

  // State for airports and nonprofits lists
  const [airports, setAirports] = useState([]);
  const [nonprofits, setNonprofits] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  const handleChange = (field, value) => {
    // Format phone numbers as user types
    if (field === "phone" || field === "mobile") {
      value = formatPhoneNumber(value);
    }

    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: "" });
    if (errorMessage) setErrorMessage("");
  };

  const getClientTypeOptionsForRole = (role, currentEntityType = "") => {
    const roleKey =
      role === "Admin" ? "Admin" : role === "Staff" ? "Staff" : "User";
    return CLIENT_TYPE_OPTIONS.filter((opt) => {
      if (!(CLIENT_TYPES_BY_ROLE[roleKey] || []).includes(opt.value)) {
        return false;
      }
      // AviPair Admin (client type AviPair) only assignable by AviPair Admins.
      if (opt.value === "AviPair" && !canAssignAviPairAdmin) {
        return String(currentEntityType).trim() === "AviPair";
      }
      return true;
    });
  };

  const handleAssociationChange = (associationId, field, value) => {
    const newAssociations = formData.associations.map((a) => {
      if (a.id !== associationId) return a;
      const next = { ...a, [field]: value };
      if (field === "role") {
        const allowed = (CLIENT_TYPES_BY_ROLE[value] || []).filter((type) => {
          if (type === "AviPair" && !canAssignAviPairAdmin) return false;
          return true;
        });
        if (!allowed.includes(next.entityType)) {
          next.entityType = "";
          next.entityId = "";
        }
      }
      if (field === "entityType") {
        if (
          value === "AviPair" &&
          !canAssignAviPairAdmin &&
          a.entityType !== "AviPair"
        ) {
          return a;
        }
        next.entityId = "";
      }
      return next;
    });
    setFormData({ ...formData, associations: newAssociations });
    setErrors((prev) => {
      const key = `association_${associationId}_${field}`;
      const next = { ...prev };
      if (next[key]) delete next[key];
      if (field !== "entityId" && next[`association_${associationId}_entityId`])
        delete next[`association_${associationId}_entityId`];
      return next;
    });
    if (errorMessage) setErrorMessage("");
  };

  const addAssociation = () => {
    setFormData({
      ...formData,
      associations: [
        ...formData.associations,
        { id: nextAssociationId(), role: "User", entityType: "", entityId: "" },
      ],
    });
  };

  const removeAssociation = (associationId) => {
    if (formData.associations.length <= 1) return;
    setFormData({
      ...formData,
      associations: formData.associations.filter((a) => a.id !== associationId),
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`association_${associationId}_role`];
      delete next[`association_${associationId}_entityType`];
      delete next[`association_${associationId}_entityId`];
      return next;
    });
  };

  const needsAirports = formData.associations?.some(
    (a) => a.entityType === "Airport",
  );
  const needsNonprofits = formData.associations?.some(
    (a) => a.entityType === "NonProfit",
  );

  // Fetch airports and/or nonprofits when any association needs them
  useEffect(() => {
    const fetchEntities = async () => {
      if (!authUser?.id_token) {
        return;
      }

      if (needsAirports || needsNonprofits) {
        setLoadingEntities(true);
        try {
          const [airportsResult, nonprofitsResult] = await Promise.all([
            needsAirports
              ? getAirports(authUser.id_token)
              : Promise.resolve({ data: null }),
            needsNonprofits
              ? getNonProfits(authUser.id_token)
              : Promise.resolve({ data: null }),
          ]);
          if (needsAirports) {
            let airportsData = airportsResult.data;
            if (typeof airportsData === "string") {
              try {
                airportsData = JSON.parse(airportsData);
              } catch (e) {
                airportsData = null;
              }
            }
            if (airportsData?.AirportsList)
              setAirports(airportsData.AirportsList);
            else if (Array.isArray(airportsData)) setAirports(airportsData);
            else setAirports([]);
          } else setAirports([]);
          if (needsNonprofits) {
            let nonprofitsData = nonprofitsResult.data;
            if (typeof nonprofitsData === "string") {
              try {
                nonprofitsData = JSON.parse(nonprofitsData);
              } catch (e) {
                nonprofitsData = null;
              }
            }
            if (nonprofitsData?.NonProfitsList)
              setNonprofits(nonprofitsData.NonProfitsList);
            else if (Array.isArray(nonprofitsData))
              setNonprofits(nonprofitsData);
            else setNonprofits([]);
          } else setNonprofits([]);
        } catch (err) {
          console.error("Error fetching entities:", err);
          setAirports([]);
          setNonprofits([]);
        } finally {
          setLoadingEntities(false);
        }
      } else {
        setAirports([]);
        setNonprofits([]);
      }
    };

    fetchEntities();
  }, [needsAirports, needsNonprofits, authUser?.id_token]);

  // Load user data in edit mode
  useEffect(() => {
    if (isEditMode && userDataFromState) {
      const user = userDataFromState;
      const location = user.location || user.profile?.location || {};
      const rawAssociations = user.association ?? user.profile?.association;
      let associations;
      const normalizeRole = (r) => {
        if (!r) return "User";
        const s = String(r).toLowerCase();
        if (s === "admin") return "Admin";
        if (s === "staff") return "Staff";
        return "User";
      };
      const normalizeEntityType = (t) => {
        if (!t) return "";
        const s = String(t);
        const map = {
          airport: "Airport",
          nonprofit: "NonProfit",
          pilot: "Pilot",
          user: "User",
          admin: "Admin",
          avipair: "AviPair",
        };
        return map[s.toLowerCase()] || s.charAt(0).toUpperCase() + s.slice(1);
      };
      if (Array.isArray(rawAssociations) && rawAssociations.length > 0) {
        associations = rawAssociations.map((a) => ({
          id: nextAssociationId(),
          role: normalizeRole(a.role),
          entityType: normalizeEntityType(a.entityType) || "",
          entityId: a.entityId || "",
        }));
      } else {
        const role = normalizeRole(user.role || user.profile?.role);
        const clientType =
          normalizeEntityType(user.clientType || user.profile?.clientType) ||
          "";
        const entityId = user.entityId || user.profile?.entityId || "";
        associations = [
          { id: nextAssociationId(), role, entityType: clientType, entityId },
        ];
      }
      setFormData({
        name: user.name || user.profile?.name || "",
        email: user.email || user.profile?.email || "",
        phone: user.phone || user.profile?.phone || "",
        mobile: user.mobile || user.profile?.mobile || "",
        timezone: user.timezone || user.profile?.timezone || "America/New_York",
        locale: user.locale || user.profile?.locale || "en-US",
        associations,
        employeeId: user.employeeId || user.profile?.employeeId || "",
        preference: user.preference || user.profile?.preference || "",
        city: location.city || user.city || user.profile?.city || "",
        state: location.state || user.state || user.profile?.state || "",
        locationId:
          location.locationId ||
          user.locationId ||
          user.profile?.locationId ||
          "",
      });
    }
  }, [isEditMode, userDataFromState]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.state) {
      newErrors.state = "State is required";
    }

    if (!formData.timezone) {
      newErrors.timezone = "Timezone is required";
    }

    if (!formData.locale) {
      newErrors.locale = "Locale is required";
    }

    if (!formData.associations?.length) {
      newErrors.associations = "At least one role/association is required";
    } else {
      formData.associations.forEach((a) => {
        if (!a.role) newErrors[`association_${a.id}_role`] = "Role is required";
        if (!a.entityType)
          newErrors[`association_${a.id}_entityType`] =
            "Client type is required";
        if (
          a.entityType === "AviPair" &&
          !canAssignAviPairAdmin &&
          !isEditMode
        ) {
          newErrors[`association_${a.id}_entityType`] =
            "Only AviPair Admins can assign the AviPair Admin role";
        }
        if (
          a.entityType === "AviPair" &&
          !canAssignAviPairAdmin &&
          isEditMode
        ) {
          const originalHadAviPair = (userDataFromState?.association ||
            userDataFromState?.associations ||
            [])
            .concat(
              userDataFromState?.role && userDataFromState?.entityType
                ? [
                    {
                      role: userDataFromState.role,
                      entityType: userDataFromState.entityType,
                    },
                  ]
                : [],
            )
            .some((orig) => isAviPairAdminAssociation(orig));
          if (!originalHadAviPair) {
            newErrors[`association_${a.id}_entityType`] =
              "Only AviPair Admins can assign the AviPair Admin role";
          }
        }
        if (
          (a.entityType === "Airport" || a.entityType === "NonProfit") &&
          !a.entityId
        ) {
          newErrors[`association_${a.id}_entityId`] =
            a.entityType === "Airport"
              ? "Please select an airport"
              : "Please select a non-profit organization";
        }
      });
    }

    // Validate phone number if provided
    if (formData.phone && formData.phone.trim()) {
      const phoneError = validatePhoneNumber(formData.phone);
      if (phoneError) {
        newErrors.phone = phoneError;
      }
    }

    // Validate mobile number if provided
    if (formData.mobile && formData.mobile.trim()) {
      const mobileError = validatePhoneNumber(formData.mobile);
      if (mobileError) {
        newErrors.mobile = mobileError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!authUser || !authUser.id_token) {
      const errorMsg =
        "You must be logged in to add users. Please sign in again.";
      setErrorMessage(errorMsg);
      // Dispatch error notification
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Error",
            message: errorMsg,
          },
        }),
      );
      return;
    }

    // Show info notification when form is submitted
    // This will be automatically closed when success notification arrives
    window.dispatchEvent(
      new CustomEvent("globalNotification", {
        detail: {
          type: "info",
          title: "Processing",
          message: isEditMode
            ? "Updating user... Please wait for confirmation."
            : "Saving user... Please wait for confirmation.",
          removeOnSuccess: true, // Flag to remove this when success arrives
        },
      }),
    );

    setLoading(true);
    setErrorMessage("");

    try {
      const associations = (formData.associations || [])
        .filter((a) => a.role && a.entityType)
        .map((a) => ({
          role: a.role,
          entityType: a.entityType,
          entityId: a.entityId || undefined,
        }));

      const userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        mobile: formData.mobile,
        timezone: formData.timezone,
        locale: formData.locale,
        employeeId: formData.employeeId,
        associations,
        location: {
          city: formData.city,
          state: formData.state,
          locationId: formData.locationId,
        },
      };

      const result = await saveUser(
        userData,
        authUser.id_token,
        profileId || "",
      );

      if (result.success) {
        // Success notification will come from subscription event
        // Reset form and redirect
        setFormData({
          name: "",
          email: "",
          phone: "",
          mobile: "",
          timezone: "America/New_York",
          locale: "en-US",
          associations: [
            {
              id: nextAssociationId(),
              role: "User",
              entityType: "",
              entityId: "",
            },
          ],
          employeeId: "",
          preference: "",
          city: "",
          state: "",
          locationId: "",
        });
        setErrors({});

        // Redirect back to users listing after showing success message
        setTimeout(() => {
          navigate("/dashboard/users");
        }, 1500);
      } else {
        const errorMsg =
          result.message || "Failed to save user. Please try again.";
        setErrorMessage(errorMsg);
        // Error notification will come from subscription event if available
      }
    } catch (error) {
      console.error("Error saving user:", error);
      const errorMsg =
        error.message || "Failed to save user. Please try again.";
      setErrorMessage(errorMsg);
      // Error notification will come from subscription event if available
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/users");
  };

  return (
    <SoftBox py={2}>
      {/* Page Header */}
      <SoftBox mb={2}>
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold">
              {isEditMode ? "Edit User" : "Add New User"}
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              {isEditMode
                ? "Update user account information"
                : "Create a new user account with the required information"}
            </SoftTypography>
          </SoftBox>
          <SoftButton
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            <Icon sx={{ mr: 1 }}>arrow_back</Icon>
            Back to Users
          </SoftButton>
        </SoftBox>
      </SoftBox>

      {/* Error Messages (for inline display) */}
      {errorMessage && (
        <SoftBox mb={2}>
          <Alert severity="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        </SoftBox>
      )}

      {/* Form Card */}
      <Card>
        <SoftBox p={2.5}>
          <Grid container spacing={1.5}>
            {/* Associations: single block with all role + entity type + entity rows */}
            <Grid item xs={12}>
              <SoftBox
                p={2}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <SoftTypography variant="button" fontWeight="bold">
                    Roles &amp; associations *
                  </SoftTypography>
                  <SoftButton
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={addAssociation}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>add</Icon>
                    Add association
                  </SoftButton>
                </SoftBox>
                {errors.associations && (
                  <SoftTypography
                    variant="caption"
                    color="error"
                    display="block"
                    mb={1}
                  >
                    {errors.associations}
                  </SoftTypography>
                )}

                {(formData.associations || []).map((assn, index) => (
                  <SoftBox key={assn.id} mt={index > 0 ? 2 : 0}>
                    <Grid container spacing={1} alignItems="flex-end">
                      <Grid item xs={12} md={3}>
                        <SoftBox mb={0.5}>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Role
                          </SoftTypography>
                        </SoftBox>
                        <SoftSelect
                          placeholder="Select Role"
                          options={ROLE_OPTIONS}
                          value={ROLE_OPTIONS.find(
                            (opt) => opt.value === assn.role,
                          )}
                          onChange={(option) =>
                            handleAssociationChange(
                              assn.id,
                              "role",
                              option?.value || "",
                            )
                          }
                          error={!!errors[`association_${assn.id}_role`]}
                        />
                        {errors[`association_${assn.id}_role`] && (
                          <SoftTypography
                            variant="caption"
                            color="error"
                            mt={0.25}
                            display="block"
                          >
                            {errors[`association_${assn.id}_role`]}
                          </SoftTypography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <SoftBox mb={0.5}>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Client type
                          </SoftTypography>
                        </SoftBox>
                        <SoftSelect
                          placeholder="Select type"
                          options={getClientTypeOptionsForRole(
                            assn.role,
                            assn.entityType,
                          )}
                          value={CLIENT_TYPE_OPTIONS.find(
                            (opt) => opt.value === assn.entityType,
                          )}
                          onChange={(option) =>
                            handleAssociationChange(
                              assn.id,
                              "entityType",
                              option?.value || "",
                            )
                          }
                          error={!!errors[`association_${assn.id}_entityType`]}
                        />
                        {errors[`association_${assn.id}_entityType`] && (
                          <SoftTypography
                            variant="caption"
                            color="error"
                            mt={0.25}
                            display="block"
                          >
                            {errors[`association_${assn.id}_entityType`]}
                          </SoftTypography>
                        )}
                      </Grid>
                      {assn.entityType === "Airport" && (
                        <Grid item xs={12} md={4}>
                          <SoftBox mb={0.5}>
                            <SoftTypography variant="caption" fontWeight="bold">
                              Airport *
                            </SoftTypography>
                          </SoftBox>
                          {loadingEntities ? (
                            <SoftBox display="flex" alignItems="center" gap={1}>
                              <CircularProgress size={16} />
                              <SoftTypography variant="caption">
                                Loading...
                              </SoftTypography>
                            </SoftBox>
                          ) : (
                            <SoftSelect
                              placeholder="Select Airport"
                              options={airports.map((airport) => ({
                                value:
                                  airport.airportId ||
                                  airport.entityId ||
                                  airport.id,
                                label:
                                  airport.name ||
                                  `Airport ${
                                    airport.airportId ||
                                    airport.entityId ||
                                    airport.id
                                  }`,
                              }))}
                              value={airports
                                .map((a) => ({
                                  value: a.airportId || a.entityId || a.id,
                                  label:
                                    a.name ||
                                    `Airport ${a.airportId || a.entityId || a.id}`,
                                }))
                                .find((opt) => opt.value === assn.entityId)}
                              onChange={(option) =>
                                handleAssociationChange(
                                  assn.id,
                                  "entityId",
                                  option?.value || "",
                                )
                              }
                              error={
                                !!errors[`association_${assn.id}_entityId`]
                              }
                            />
                          )}
                          {errors[`association_${assn.id}_entityId`] && (
                            <SoftTypography
                              variant="caption"
                              color="error"
                              mt={0.25}
                              display="block"
                            >
                              {errors[`association_${assn.id}_entityId`]}
                            </SoftTypography>
                          )}
                        </Grid>
                      )}
                      {assn.entityType === "NonProfit" && (
                        <Grid item xs={12} md={4}>
                          <SoftBox mb={0.5}>
                            <SoftTypography variant="caption" fontWeight="bold">
                              Non-Profit *
                            </SoftTypography>
                          </SoftBox>
                          {loadingEntities ? (
                            <SoftBox display="flex" alignItems="center" gap={1}>
                              <CircularProgress size={16} />
                              <SoftTypography variant="caption">
                                Loading...
                              </SoftTypography>
                            </SoftBox>
                          ) : (
                            <SoftSelect
                              placeholder="Select Non-Profit"
                              options={nonprofits.map((np) => ({
                                value: np.nonprofitId || np.entityId || np.id,
                                label:
                                  np.name ||
                                  `Non-Profit ${np.nonprofitId || np.entityId || np.id}`,
                              }))}
                              value={nonprofits
                                .map((np) => ({
                                  value: np.nonprofitId || np.entityId || np.id,
                                  label:
                                    np.name ||
                                    `Non-Profit ${np.nonprofitId || np.entityId || np.id}`,
                                }))
                                .find((opt) => opt.value === assn.entityId)}
                              onChange={(option) =>
                                handleAssociationChange(
                                  assn.id,
                                  "entityId",
                                  option?.value || "",
                                )
                              }
                              error={
                                !!errors[`association_${assn.id}_entityId`]
                              }
                            />
                          )}
                          {errors[`association_${assn.id}_entityId`] && (
                            <SoftTypography
                              variant="caption"
                              color="error"
                              mt={0.25}
                              display="block"
                            >
                              {errors[`association_${assn.id}_entityId`]}
                            </SoftTypography>
                          )}
                        </Grid>
                      )}
                      <Grid
                        item
                        xs={12}
                        md={
                          assn.entityType === "Airport" ||
                          assn.entityType === "NonProfit"
                            ? 2
                            : 6
                        }
                        sx={{
                          display: "flex",
                          alignItems: "flex-end",
                          pb: 0.5,
                        }}
                      >
                        <SoftButton
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => removeAssociation(assn.id)}
                          disabled={formData.associations?.length <= 1}
                        >
                          <Icon sx={{ fontSize: "1rem", mr: 0.5 }}>delete</Icon>
                          Remove
                        </SoftButton>
                      </Grid>
                    </Grid>
                  </SoftBox>
                ))}
              </SoftBox>
            </Grid>

            <Grid item xs={12}></Grid>

            {/* Name */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Name *
                </SoftTypography>
              </SoftBox>
              <SoftInput
                placeholder="Enter full name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                error={!!errors.name}
              />
              {errors.name && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  mt={0.25}
                  display="block"
                >
                  {errors.name}
                </SoftTypography>
              )}
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Email *
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="email"
                placeholder="email@example.com"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                error={!!errors.email}
              />
              {errors.email && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  mt={0.25}
                  display="block"
                >
                  {errors.email}
                </SoftTypography>
              )}
            </Grid>

            {/* Employee ID */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Employee ID
                </SoftTypography>
              </SoftBox>
              <SoftInput
                placeholder="Enter employee ID (optional)"
                value={formData.employeeId}
                onChange={(e) => handleChange("employeeId", e.target.value)}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Phone
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="tel"
                placeholder="(XXX) XXX-XXXX"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                error={!!errors.phone}
              />
              {errors.phone && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  mt={0.25}
                  display="block"
                >
                  {errors.phone}
                </SoftTypography>
              )}
            </Grid>

            {/* Mobile */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Mobile
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="tel"
                placeholder="(XXX) XXX-XXXX"
                value={formData.mobile}
                onChange={(e) => handleChange("mobile", e.target.value)}
                error={!!errors.mobile}
              />
              {errors.mobile && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  mt={0.25}
                  display="block"
                >
                  {errors.mobile}
                </SoftTypography>
              )}
            </Grid>

            {/* City */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  City *
                </SoftTypography>
              </SoftBox>
              <SoftInput
                placeholder="Enter city"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                error={!!errors.city}
              />
              {errors.city && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  mt={0.25}
                  display="block"
                >
                  {errors.city}
                </SoftTypography>
              )}
            </Grid>

            {/* State */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  State *
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                placeholder="Select State"
                options={US_STATES}
                value={US_STATES.find((opt) => opt.value === formData.state)}
                onChange={(option) =>
                  handleChange("state", option?.value || "")
                }
                error={!!errors.state}
              />
              {errors.state && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  mt={0.25}
                  display="block"
                >
                  {errors.state}
                </SoftTypography>
              )}
            </Grid>

            {/* Location ID */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Location ID
                </SoftTypography>
              </SoftBox>
              <SoftInput
                placeholder="Enter location ID (optional)"
                value={formData.locationId}
                onChange={(e) => handleChange("locationId", e.target.value)}
              />
            </Grid>

            {/* Timezone */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Timezone *
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                placeholder="Select Timezone"
                options={TIMEZONE_OPTIONS}
                value={TIMEZONE_OPTIONS.find(
                  (opt) => opt.value === formData.timezone,
                )}
                onChange={(option) =>
                  handleChange("timezone", option?.value || "")
                }
                error={!!errors.timezone}
              />
              {errors.timezone && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  mt={0.25}
                  display="block"
                >
                  {errors.timezone}
                </SoftTypography>
              )}
            </Grid>

            {/* Locale */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Locale *
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                placeholder="Select Locale"
                options={LOCALE_OPTIONS}
                value={LOCALE_OPTIONS.find(
                  (opt) => opt.value === formData.locale,
                )}
                onChange={(option) =>
                  handleChange("locale", option?.value || "")
                }
                error={!!errors.locale}
              />
              {errors.locale && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  mt={0.25}
                  display="block"
                >
                  {errors.locale}
                </SoftTypography>
              )}
            </Grid>

            {/* Preference */}
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Preference
                </SoftTypography>
              </SoftBox>
              <SoftInput
                placeholder="Enter preferences (optional)"
                value={formData.preference}
                onChange={(e) => handleChange("preference", e.target.value)}
                multiline
                rows={2}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <SoftBox
                mt={1.5}
                display="flex"
                justifyContent="flex-end"
                gap={2}
              >
                <SoftButton
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </SoftButton>
                <SoftButton
                  variant="gradient"
                  color="info"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <CircularProgress
                        size={16}
                        sx={{ mr: 1 }}
                        color="inherit"
                      />
                      {isEditMode ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    <>
                      <Icon sx={{ mr: 1 }}>{isEditMode ? "save" : "add"}</Icon>
                      {isEditMode ? "Update User" : "Create User"}
                    </>
                  )}
                </SoftButton>
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>
      </Card>
    </SoftBox>
  );
}

export default AddUserPage;
